import { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FlightsFilterForm } from 'legacyfrontend/components/filters';
import { useTranslation } from 'react-i18next';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';

import PackagesApi from 'legacyfrontend/api/paquetes';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';

import { flightAirportsAreSame, flightAirportSearchKey } from 'legacyfrontend/helpers/flightAirportsAreSame';
import { validateFlightsSearchFromForm, getMissingFlightFieldLabels } from 'legacyfrontend/helpers/validateFlightsSearchSubmit';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import {
  getNormalizedFlightSearchState,
  getStoredFlightFormDraft,
  saveFlightFormDraft,
  saveResolvedFlightInfo,
} from 'legacyfrontend/flights/search/flightSearchStorage';
import {
  normalizePassengersParam,
  parseFlightDateParam,
  parseFlightSearchParams,
  serializeFlightSearchParams,
} from 'legacyfrontend/flights/search/flightSearchParams';
import ErrorSnackbar from '../../ErrorSnackbar';

const { BUSCAR_X_VUELOS } = ROUTE_KEYS;

async function resolveAirportDbId(airportLike) {
  if (airportLike?.id != null && !Number.isNaN(Number(airportLike.id))) {
    return Number(airportLike.id);
  }
  const q = (airportLike?.cityName || airportLike?.cityCode || '').trim();
  if (q.length < 2) return null;
  try {
    const list = await PackagesApi.getAirports({ cityName: q });
    const arr = Array.isArray(list) ? list : [];
    if (arr.length === 0) return null;
    const match =
      (airportLike.cityCode &&
        arr.find(
          (a) =>
            a.cityCode === airportLike.cityCode &&
            (!airportLike.airportCode || a.airportCode === airportLike.airportCode)
        )) ||
      (airportLike.airportCode && arr.find((a) => a.airportCode === airportLike.airportCode)) ||
      arr[0];
    return match?.id != null && !Number.isNaN(Number(match.id)) ? Number(match.id) : null;
  } catch {
    return null;
  }
}

const formatDateLocal = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const dateToLocalString = (date) => {
  if (!date) return null;
  return formatDateLocal(date);
};

const initialFlightSetter = (params) => {
  const searchParams = parseFlightSearchParams(params);

  const result = {
    origin:
      searchParams.originId && searchParams.originName
        ? {
          id:
            searchParams.originIdn && !isNaN(Number(searchParams.originIdn))
              ? Number(searchParams.originIdn)
              : null,
          cityCode: searchParams.originId,
          cityName: searchParams.originName.trim(),
        }
        : null,
    destination:
      searchParams.destinationId && searchParams.destinationName
        ? {
          id:
            searchParams.destinationIdn && !isNaN(Number(searchParams.destinationIdn))
              ? Number(searchParams.destinationIdn)
              : null,
          cityCode: searchParams.destinationId,
          cityName: searchParams.destinationName.trim(),
        }
        : null,
    passengers: normalizePassengersParam(searchParams.passengers),
    departureDate: parseFlightDateParam(searchParams.departureDate),
    returnDate: parseFlightDateParam(searchParams.returnDate),
    direct: false,
    baggage: false,
    cabin: 'Y',
    currency: searchParams.currency || 'USD',
    isOnlyOneWay: searchParams.isOnlyOneWay === 'true',
  };

  return result;
};

const hasCompleteFlightDraft = (draft) =>
  Boolean(draft?.origin && draft?.destination && draft?.departureDate);

const FlightsSearch = forwardRef(
  (
    {
      isOnTargetPath,
      background,
      text,
      onCloseMobileSearch,
      onSearchStart,
      onSearchComplete,
      isMobile,
      isLoading = false,
    },
    ref
  ) => {
    const { t } = useTranslation('layout');
    const history = useHistory();
    const { pathname, search } = useLocation();
    const { registerOnSearch } = useGlobal();
    const {
      setOneWayFlight,
      isLoading: flightContextLoading,
      clearFlightSelectionStateForNewSearch,
    } = useFlightContext();

    const [isUpdatingFlightInfo, setIsUpdatingFlightInfo] = useState(false);
    const isUpdatingFlightInfoRef = useRef(false);
    const lastUpdateFlightInfoRef = useRef({ originKey: '', destinationKey: '', timestamp: 0 });
    const updateFlightInfoCallCountRef = useRef(0);
    const updateFlightInfoMaxCallsRef = useRef(0);
    const isBlockedPermanentlyRef = useRef(false);

    const [errorMessage, setErrorMessage] = useState('');
    const [openToast, setOpenToast] = useState(false);

    const showError = useCallback((message) => {
      setErrorMessage(message);
      setOpenToast(true);
    }, []);

    const handleCloseToast = useCallback(() => {
      setOpenToast(false);
    }, []);

    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);

    const initialFlightFormInput = useMemo(
      () =>
        initialFlightSetter(
          !!search && pathname === BUSCAR_X_VUELOS ? search : undefined
        ),
      [search, pathname]
    );

    const getInitialState = () => {
      // URL flight search params take priority over sessionStorage
      if (
        initialFlightFormInput.origin &&
        initialFlightFormInput.destination &&
        initialFlightFormInput.departureDate
      ) {
        return initialFlightFormInput;
      }

      if (isClient) {
        try {
          const storedDraft = getStoredFlightFormDraft();
          if (hasCompleteFlightDraft(storedDraft)) return storedDraft;
        } catch (error) {
        }
      }
      return initialFlightFormInput;
    };

    const [flightFormInput, setFlightFormInput] = useState(getInitialState);

    useEffect(() => {
      // Skip storage restore when URL has valid flight search params
      if (
        initialFlightFormInput.origin &&
        initialFlightFormInput.destination &&
        initialFlightFormInput.departureDate
      ) {
        return;
      }

      if (isClient) {
        try {
          const storedDraft = getStoredFlightFormDraft();
          if (hasCompleteFlightDraft(storedDraft)) {
            setFlightFormInput(prev => {
              const newState = {
                ...prev,
                origin: storedDraft.origin || prev.origin,
                destination: storedDraft.destination || prev.destination,
                passengers: storedDraft.passengers || prev.passengers,
                departureDate: storedDraft.departureDate || prev.departureDate,
                returnDate: storedDraft.returnDate || prev.returnDate,
                isOnlyOneWay: storedDraft.isOnlyOneWay !== undefined ? storedDraft.isOnlyOneWay : prev.isOnlyOneWay,
                currency: storedDraft.currency || prev.currency || 'USD'
              };
              return newState;
            });
          }
        } catch (error) {
        }
      }
    }, []);

    const lastSavedFlightFormInputRef = useRef(null);

    useEffect(() => {
      if (isClient && flightFormInput) {
        const currentKey = JSON.stringify({
          origin: flightAirportSearchKey(flightFormInput.origin),
          destination: flightAirportSearchKey(flightFormInput.destination),
          departureDate: dateToLocalString(flightFormInput.departureDate),
          returnDate: dateToLocalString(flightFormInput.returnDate),
          isOnlyOneWay: flightFormInput.isOnlyOneWay,
          passengers: flightFormInput.passengers?.join(',')
        });

        if (lastSavedFlightFormInputRef.current === currentKey) {
          return;
        }

        try {
          saveFlightFormDraft(flightFormInput);
          lastSavedFlightFormInputRef.current = currentKey;
        } catch (error) {
        }
      }
    }, [flightFormInput, isMobile]);

    useEffect(() => {
      const hasUrlParams = search && search.length > 1;

      if (hasUrlParams && pathname === BUSCAR_X_VUELOS) {
        const urlParams = parseFlightSearchParams(search);
        const {
          originId,
          originName,
          originIdn,
          destinationId,
          destinationName,
          destinationIdn,
          departureDate,
          returnDate,
          passengers,
          isOnlyOneWay,
          currency,
        } = urlParams;
        const passengersParam = Array.isArray(passengers) ? passengers.join(',') : passengers;

        setFlightFormInput((prev) => {
          const needsUpdate =
            (originId && originName && (!prev.origin || prev.origin.cityCode !== originId)) ||
            (destinationId && destinationName && (!prev.destination || prev.destination.cityCode !== destinationId)) ||
            (departureDate && (!prev.departureDate || formatDateLocal(prev.departureDate) !== departureDate)) ||
            (returnDate && (!prev.returnDate || formatDateLocal(prev.returnDate) !== returnDate)) ||
            (passengersParam && (!prev.passengers || prev.passengers.join(',') !== passengersParam)) ||
            (isOnlyOneWay != null && prev.isOnlyOneWay !== (isOnlyOneWay === 'true')) ||
            (currency && prev.currency !== currency);

          if (!needsUpdate) return prev;

          const newState = { ...prev };

          if (originId && originName) {
            newState.origin = {
              id: originIdn && !isNaN(Number(originIdn)) ? Number(originIdn) : null,
              cityCode: originId,
              cityName: originName.trim(),
            };
          }

          if (destinationId && destinationName) {
            newState.destination = {
              id: destinationIdn && !isNaN(Number(destinationIdn)) ? Number(destinationIdn) : null,
              cityCode: destinationId,
              cityName: destinationName.trim(),
            };
          }

          if (departureDate) {
            newState.departureDate = parseFlightDateParam(departureDate);
          }

          if (returnDate) {
            newState.returnDate = parseFlightDateParam(returnDate);
          }

          if (passengers) {
            newState.passengers = normalizePassengersParam(passengersParam);
          }

          if (isOnlyOneWay != null) {
            const newIsOnlyOneWay = isOnlyOneWay === 'true';
            newState.isOnlyOneWay = newIsOnlyOneWay;
            if (newIsOnlyOneWay && prev.returnDate) {
              newState.returnDate = null;
            }
            if (newIsOnlyOneWay !== prev.isOnlyOneWay) {
              setOneWayFlight(newIsOnlyOneWay);
            }
          }

          if (currency) {
            newState.currency = currency;
          }

          return newState;
        });
      }
    }, [search, pathname]);

    useImperativeHandle(ref, () => ({
      openFirstDialog: () => {
      },
      getFormInput: () => flightFormInput,
      triggerSearch: () => {
        handleSearchFlightsButton({ preventDefault: () => { } });
      },
      isFormValid: () => {
        const fi =
          flightFormInput?.passengers?.length > 0
            ? flightFormInput
            : { ...flightFormInput, passengers: [30] };
        return validateFlightsSearchFromForm(fi) === null;
      },
    }));


    const updateFlightInfo = useCallback(
      async (origin, destination, currentFlightFormInput) => {
        if (!origin || !destination || isUpdatingFlightInfoRef.current) return;

        if (flightAirportsAreSame(origin, destination)) {
          return;
        }

        if (isBlockedPermanentlyRef.current) {
          return;
        }

        const now = Date.now();
        const timeSinceLastCall = now - lastUpdateFlightInfoRef.current.timestamp;
        const originKey = flightAirportSearchKey(origin);
        const destinationKey = flightAirportSearchKey(destination);
        const isSameCall =
          lastUpdateFlightInfoRef.current.originKey === originKey &&
          lastUpdateFlightInfoRef.current.destinationKey === destinationKey;

        if (isSameCall) {
          if (timeSinceLastCall < 5000) {
            updateFlightInfoCallCountRef.current += 1;
            updateFlightInfoMaxCallsRef.current = Math.max(
              updateFlightInfoMaxCallsRef.current,
              updateFlightInfoCallCountRef.current
            );

            if (updateFlightInfoCallCountRef.current > 3) {
              isBlockedPermanentlyRef.current = true;
              return;
            }
            return;
          } else {
            updateFlightInfoCallCountRef.current = 0;
          }
        } else {
          updateFlightInfoCallCountRef.current = 0;
        }

        try {
          const parsed = getNormalizedFlightSearchState().flightInfo;
          if (parsed?.origin && parsed?.destination) {
            const parsedOriginKey = flightAirportSearchKey(parsed.origin);
            const parsedDestKey = flightAirportSearchKey(parsed.destination);
            if (
              parsedOriginKey === originKey &&
              parsedDestKey === destinationKey &&
              parsed.originCountryCode &&
              parsed.destinationCountryCode
            ) {
              lastUpdateFlightInfoRef.current = {
                originKey,
                destinationKey,
                timestamp: now,
              };
              return;
            }
          }
        } catch (e) {
        }

        try {
          setIsUpdatingFlightInfo(true);
          isUpdatingFlightInfoRef.current = true;

          let resolvedOriginId =
            origin.id != null && !Number.isNaN(Number(origin.id)) ? Number(origin.id) : null;
          let resolvedDestId =
            destination.id != null && !Number.isNaN(Number(destination.id))
              ? Number(destination.id)
              : null;

          if (!resolvedOriginId || !resolvedDestId) {
            const [rid, did] = await Promise.all([
              resolvedOriginId ? Promise.resolve(resolvedOriginId) : resolveAirportDbId(origin),
              resolvedDestId ? Promise.resolve(resolvedDestId) : resolveAirportDbId(destination),
            ]);
            resolvedOriginId = resolvedOriginId || rid;
            resolvedDestId = resolvedDestId || did;
          }

          if (!resolvedOriginId || !resolvedDestId) {
            updateFlightInfoCallCountRef.current = 0;
            return null;
          }

          const [originData, destinationData] = await Promise.all([
            PackagesApi.getAirportById(resolvedOriginId),
            PackagesApi.getAirportById(resolvedDestId),
          ]);

          if (!originData || !destinationData) {
            updateFlightInfoCallCountRef.current = 0;
            return null;
          }

          const isInternational = originData.countryCode !== destinationData.countryCode;
          const flightInfo = {
            originCountryCode: originData.countryCode,
            destinationCountryCode: destinationData.countryCode,
            origin: {
              id: originData.id,
              airportCode: originData.airportCode,
              airportName: originData.airportName,
              cityCode: originData.cityCode,
              cityName: originData.cityName,
              countryCode: originData.countryCode,
            },
            destination: {
              id: destinationData.id,
              airportCode: destinationData.airportCode,
              airportName: destinationData.airportName,
              cityCode: destinationData.cityCode,
              cityName: destinationData.cityName,
              countryCode: destinationData.countryCode,
            },
            departureDate: currentFlightFormInput.departureDate
              ? new Date(currentFlightFormInput.departureDate)
              : null,
            returnDate: currentFlightFormInput.isOnlyOneWay
              ? null
              : (currentFlightFormInput.returnDate ? new Date(currentFlightFormInput.returnDate) : null),
            passengers: currentFlightFormInput.passengers,
            isOnlyOneWay: currentFlightFormInput.isOnlyOneWay,
            isInternational,
          };

          saveResolvedFlightInfo(flightInfo);
          updateFlightInfoCallCountRef.current = 0;
          lastUpdateFlightInfoRef.current = {
            originKey,
            destinationKey,
            timestamp: Date.now(),
          };
          return flightInfo;
        } catch (error) {
          updateFlightInfoCallCountRef.current = 0;
        } finally {
          setIsUpdatingFlightInfo(false);
          isUpdatingFlightInfoRef.current = false;
        }
      },
      []
    );

    const handleFlightOrigin = useCallback(
      async (origin) => {
        if (!origin) {
          setFlightFormInput((state) => ({
            ...state,
            origin: null,
            departureDate: null,
            returnDate: null,
          }));
          return;
        }

        if (flightFormInput?.origin && flightAirportsAreSame(flightFormInput.origin, origin)) {
          return;
        }

        if (isBlockedPermanentlyRef.current) {
          setFlightFormInput((state) => ({ ...state, origin }));
          return;
        }

        setFlightFormInput((state) => ({ ...state, origin }));
      },
      [flightFormInput]
    );

    const handleFlightDestination = useCallback(
      async (destination) => {
        if (!destination) {
          setFlightFormInput((state) => ({ ...state, destination: null }));
          return;
        }

        if (flightFormInput?.destination && flightAirportsAreSame(flightFormInput.destination, destination)) {
          return;
        }

        if (isBlockedPermanentlyRef.current) {
          setFlightFormInput((state) => ({ ...state, destination }));
          return;
        }

        setFlightFormInput((state) => ({ ...state, destination }));
      },
      [flightFormInput]
    );

    const handleFlightPassenger = (passengers) => {
      setFlightFormInput((state) => ({ ...state, passengers }));
    };

    const handleFlightsDateRangeSelect = (departureDate, returnDate) => {
      setFlightFormInput((state) => ({ ...state, departureDate, returnDate }));
    };

    const handleIsOnlyOneWay = (isOnlyOneWay) => {
      setFlightFormInput((state) => ({ ...state, isOnlyOneWay }));
      setOneWayFlight(isOnlyOneWay);
    };

    const handleCurrencyChange = (currency) => {
      setFlightFormInput((state) => ({ ...state, currency }));
    };

    const handleOccupationChange = (totalTourists) => {
    };

    const [isDestinationEmpty, setIsDestinationEmpty] = useState(false);
    const [isOriginEmpty, setIsOriginEmpty] = useState(false);
    const [isDatesEmpty, setIsDatesEmpty] = useState(false);
    const [isTuristasEmpty, setIsTuristasEmpty] = useState(false);

    useEffect(() => {
      if (!flightFormInput) return;

      if (flightFormInput.origin) {
        setIsOriginEmpty(false);
      }
      if (flightFormInput.destination) {
        setIsDestinationEmpty(false);
      }
      if (flightFormInput.departureDate) {
        setIsDatesEmpty(false);
      }
      if (flightFormInput.passengers && flightFormInput.passengers.length > 0) {
        setIsTuristasEmpty(false);
      }
    }, [
      flightFormInput?.origin,
      flightFormInput?.destination,
      flightFormInput?.departureDate,
      flightFormInput?.passengers,
    ]);

    const getFlightsQueryString = useCallback((overrideInput) => {
      const fi = overrideInput ?? flightFormInput;
      return serializeFlightSearchParams({ ...fi, cache: getCacheStatus() });
    }, [flightFormInput]);

    const getFlightsQueryStringWithRefresh = useCallback(
      (overrideInput) => {
        const fi = overrideInput ?? flightFormInput;
        return serializeFlightSearchParams({ ...fi, cache: getCacheStatus() }, { includeRefresh: true });
      },
      [flightFormInput]
    );

    const linkToFLights = useMemo(
      () =>
        flightFormInput?.destination && flightFormInput?.origin
          ? {
            pathname: ROUTE_KEYS.BUSCAR_X_VUELOS,
          }
          : '#',
      [flightFormInput]
    );

    const handleMobileSearchComplete = () => {
      setIsMobileSearchActive(false);
      if (onCloseMobileSearch) {
        onCloseMobileSearch();
      }
      if (onSearchComplete) {
        onSearchComplete();
      }
    };

    const clearSavedState = () => {
      if (isClient) {
        try {
        } catch (error) {
        }
      }
    };

    const handleSearchFlightsButton = useCallback(
      async (e) => {
        e.preventDefault();

        const inputForSubmit =
          !flightFormInput?.passengers ||
          !Array.isArray(flightFormInput.passengers) ||
          flightFormInput.passengers.length === 0
            ? { ...flightFormInput, passengers: [30] }
            : flightFormInput;

        if (inputForSubmit !== flightFormInput) {
          setFlightFormInput(inputForSubmit);
        }

        const submitError = validateFlightsSearchFromForm(inputForSubmit);
        if (submitError) {
          const missingLabels = getMissingFlightFieldLabels(inputForSubmit);

          // Mark all missing fields for inline helper text
          setIsOriginEmpty(missingLabels.includes('origen'));
          setIsDestinationEmpty(missingLabels.includes('destino'));
          setIsDatesEmpty(missingLabels.includes('fechas'));

          if (missingLabels.length > 0) {
            // Summarize all missing fields in a single snackbar
            if (missingLabels.length === 1) {
              showError(`Completá ${missingLabels[0]} para continuar.`);
            } else {
              const last = missingLabels[missingLabels.length - 1];
              const previousLabels = missingLabels.slice(0, -1).join(', ');
              showError(`Completá ${previousLabels} y ${last} para continuar.`);
            }
          } else {
            // Non-missing-field error (e.g. SAME_ORIGIN_DESTINATION)
            showError(submitError.message);
          }
          return;
        }

        try {
          if (onSearchStart) {
            onSearchStart();
          }

          let resolvedFlightInfo = null;
          if (!isBlockedPermanentlyRef.current && !isUpdatingFlightInfoRef.current) {
            resolvedFlightInfo = await updateFlightInfoRef.current(
              inputForSubmit.origin,
              inputForSubmit.destination,
              inputForSubmit
            );
          }

          clearFlightSelectionStateForNewSearch(resolvedFlightInfo || inputForSubmit);

          const queryStringWithRefresh = getFlightsQueryStringWithRefresh(inputForSubmit);

          if (isMobile) {
            setTimeout(() => {
              history.push(linkToFLights.pathname + queryStringWithRefresh);
              registerOnSearch();
              clearSavedState();
              handleMobileSearchComplete();
            }, 500);
          } else {
            history.push(linkToFLights.pathname + queryStringWithRefresh);
            registerOnSearch();
            clearSavedState();
          }
        } catch (error) {
          showError(error?.message || 'Error al procesar la búsqueda');
          if (onSearchComplete) {
            onSearchComplete();
          }
        }
      },
      [
        linkToFLights,
        getFlightsQueryString,
        getFlightsQueryStringWithRefresh,
        history,
        registerOnSearch,
        flightFormInput,
        showError,
        isMobile,
        clearFlightSelectionStateForNewSearch,
        handleMobileSearchComplete,
      ]
    );

    const isSearchButtonDisabled = () => {
      return (
        !flightFormInput.origin || 
        !flightFormInput.destination || 
        !flightFormInput.departureDate
      );
    };

    const updateFlightInfoRef = useRef(updateFlightInfo);
    useEffect(() => {
      updateFlightInfoRef.current = updateFlightInfo;
    }, [updateFlightInfo]);

    if (isMobile) {
      return (
        <>
          <div className='package-search__form__container'>
            <div className='package-search__form__fields'>
              <FlightsFilterForm
                handleOriginSelect={handleFlightOrigin}
                handleDestinationSelect={handleFlightDestination}
                handleDateRangeSelect={handleFlightsDateRangeSelect}
                handleDepartureDateChange={handleFlightsDateRangeSelect}
                handleReturnDateChange={handleFlightsDateRangeSelect}
                handleFlightPassenger={handleFlightPassenger}
                handleIsOnlyOneWay={handleIsOnlyOneWay}
                handleCurrencyChange={handleCurrencyChange}
                handleOccupationChange={handleOccupationChange}
                formInput={flightFormInput}
                isOriginEmpty={isOriginEmpty}
                isDestinationEmpty={isDestinationEmpty}
                setIsOriginEmpty={setIsOriginEmpty}
                setIsDestinationEmpty={setIsDestinationEmpty}
                isDatesEmpty={isDatesEmpty}
                isTuristasEmpty={false}
                setIsDatesEmpty={setIsDatesEmpty}
                onMobileSearchComplete={handleMobileSearchComplete}
                isMobileSearchActive={false}
                isLoading={flightContextLoading}
              />
            </div>
          </div>

          <ErrorSnackbar
            open={openToast}
            message={errorMessage}
            onClose={handleCloseToast}
            zIndex={1400}
          />
        </>
      );
    }

    if (!isOnTargetPath) {
      return <></>;
    }

    return (
      <>
        <div className='package-search__form__container'>
          <div
            className='package-search__form__fields'
            style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
          >
            <FlightsFilterForm
              handleOriginSelect={handleFlightOrigin}
              handleDestinationSelect={handleFlightDestination}
              handleDateRangeSelect={handleFlightsDateRangeSelect}
              handleDepartureDateChange={handleFlightsDateRangeSelect}
              handleReturnDateChange={handleFlightsDateRangeSelect}
              handleFlightPassenger={handleFlightPassenger}
              handleIsOnlyOneWay={handleIsOnlyOneWay}
              handleCurrencyChange={handleCurrencyChange}
              handleOccupationChange={handleOccupationChange}
              formInput={flightFormInput}
              isOriginEmpty={isOriginEmpty}
              isDestinationEmpty={isDestinationEmpty}
              setIsOriginEmpty={setIsOriginEmpty}
              setIsDestinationEmpty={setIsDestinationEmpty}
              isDatesEmpty={isDatesEmpty}
              isTuristasEmpty={false}
              setIsDatesEmpty={setIsDatesEmpty}
              onMobileSearchComplete={handleMobileSearchComplete}
              isMobileSearchActive={isMobileSearchActive}
              isLoading={flightContextLoading}
            />
            <div className='package-search__button-row'>
              <button
                onClick={handleSearchFlightsButton}
                disabled={!!flightContextLoading}
                className='package-search__button big-package-search__button'
                style={{
                  backgroundColor: background,
                  color: text,
                  padding: '12px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: flightContextLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  opacity: flightContextLoading ? 0.6 : 1,
                }}
              >
                <i className='fa fa-search heading__icon' />
                {flightContextLoading ? 'Buscando...' : (t('common.search') || 'Buscar')}
              </button>
            </div>
          </div>
        </div>

        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </>
    );
  }
);

export default FlightsSearch;
